import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, CalendarCheck, Send } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, Overline } from "../components/Reveal";
import PageHeader from "../components/PageHeader";
import { CLAIM_VOLUMES, CONTACT_INFO } from "../data/content";
import { api } from "../lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const fieldClass =
  "w-full bg-deepblack border border-gold/30 px-4 py-3 text-sm text-offwhite placeholder:text-muted2/60 focus:outline-none focus:border-gold transition-colors";

export default function Contact() {
  const { lang, t } = useLang();
  const p = t.pages.contact;

  const [contact, setContact] = useState({
    name: "",
    clinic_name: "",
    email: "",
    phone: "",
    monthly_claim_volume: "",
    message: "",
  });
  const [booking, setBooking] = useState({
    name: "",
    email: "",
    phone: "",
    clinic_name: "",
    preferred_date: "",
    preferred_time: "",
    notes: "",
  });
  const [loadingC, setLoadingC] = useState(false);
  const [loadingB, setLoadingB] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    setLoadingC(true);
    try {
      await axios.post(`${api}/contact`, contact);
      toast.success(p.contactSuccess);
      setContact({ name: "", clinic_name: "", email: "", phone: "", monthly_claim_volume: "", message: "" });
    } catch {
      toast.error(p.contactError);
    } finally {
      setLoadingC(false);
    }
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    setLoadingB(true);
    try {
      await axios.post(`${api}/consultation`, booking);
      toast.success(p.bookingSuccess);
      setBooking({ name: "", email: "", phone: "", clinic_name: "", preferred_date: "", preferred_time: "", notes: "" });
    } catch {
      toast.error(p.bookingError);
    } finally {
      setLoadingB(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        overline={p.overline}
        title={p.titleA}
        accent={p.accent}
        subtitle={p.subtitle}
      />

      <section className="pb-24 bg-navy">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* CONTACT INFO */}
          <Reveal className="lg:col-span-1">
            <div className="space-y-8">
              <div>
                <h3 className="font-serif text-3xl text-offwhite mb-4">{p.talkTitle}</h3>
                <p className="text-muted2 text-sm leading-relaxed">
                  {CONTACT_INFO.coverage[lang]} {p.talkBody}
                </p>
              </div>
              <div className="space-y-5">
                <a href={`tel:${CONTACT_INFO.phone}`} data-testid="contact-phone" className="flex items-center gap-4 text-offwhite hover:text-gold transition-colors">
                  <span className="h-11 w-11 flex items-center justify-center border border-gold/30 text-gold"><Phone size={18} /></span>
                  {CONTACT_INFO.phone}
                </a>
                <a href={`mailto:${CONTACT_INFO.email}`} data-testid="contact-email" className="flex items-center gap-4 text-offwhite hover:text-gold transition-colors">
                  <span className="h-11 w-11 flex items-center justify-center border border-gold/30 text-gold"><Mail size={18} /></span>
                  {CONTACT_INFO.email}
                </a>
                <div className="flex items-start gap-4 text-offwhite">
                  <span className="h-11 w-11 flex items-center justify-center border border-gold/30 text-gold shrink-0"><MapPin size={18} /></span>
                  <span className="text-sm leading-relaxed pt-2">{p.addressLine}</span>
                </div>
              </div>
            </div>
          </Reveal>

          {/* CONTACT FORM */}
          <Reveal delay={0.1} className="lg:col-span-2">
            <form onSubmit={submitContact} data-testid="contact-form" className="bg-deepblack border border-gold/20 p-8 sm:p-10 space-y-5">
              <h3 className="font-serif text-2xl text-offwhite mb-2">{p.formTitle}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input data-testid="contact-name" required placeholder={p.ph.name} className={fieldClass} value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
                <input data-testid="contact-clinic" placeholder={p.ph.clinic} className={fieldClass} value={contact.clinic_name} onChange={(e) => setContact({ ...contact, clinic_name: e.target.value })} />
                <input data-testid="contact-email-input" required type="email" placeholder={p.ph.email} className={fieldClass} value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                <input data-testid="contact-phone-input" placeholder={p.ph.phone} className={fieldClass} value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
              </div>
              <Select value={contact.monthly_claim_volume} onValueChange={(v) => setContact({ ...contact, monthly_claim_volume: v })}>
                <SelectTrigger data-testid="contact-volume-trigger" className={`${fieldClass} flex justify-between rounded-none h-auto`}>
                  <SelectValue placeholder={p.ph.volume} />
                </SelectTrigger>
                <SelectContent className="bg-deepblack border-gold/30 text-offwhite">
                  {CLAIM_VOLUMES[lang].map((v) => (
                    <SelectItem key={v} value={v} className="focus:bg-gold/15 focus:text-gold">{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <textarea data-testid="contact-message" placeholder={p.ph.message} rows={4} className={fieldClass} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} />
              <button type="submit" disabled={loadingC} data-testid="contact-submit" className="bg-gold text-deepblack px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-goldhover transition-colors inline-flex items-center gap-2 disabled:opacity-60">
                {loadingC ? p.sending : p.send} <Send size={14} />
              </button>
            </form>
          </Reveal>
        </div>

        {/* BOOKING */}
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 mt-10">
          <Reveal>
            <div className="bg-deepblack border border-gold/20 p-8 sm:p-10 grain relative overflow-hidden">
              <div className="absolute inset-0 gold-glow pointer-events-none" />
              <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <Overline>{p.bookOverline}</Overline>
                  <h3 className="font-serif text-3xl sm:text-4xl text-offwhite mt-4 mb-4 flex items-center gap-3">
                    <CalendarCheck className="text-gold" size={30} /> {p.bookTitle}
                  </h3>
                  <p className="text-muted2 text-sm leading-relaxed mb-6">{p.bookBody}</p>
                  <div className="border border-dashed border-gold/30 p-6 text-center text-muted2 text-xs uppercase tracking-[0.15em]" data-testid="calendar-placeholder">
                    {p.calendar}
                  </div>
                </div>

                <form onSubmit={submitBooking} data-testid="booking-form" className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input data-testid="booking-name" required placeholder={p.ph.name} className={fieldClass} value={booking.name} onChange={(e) => setBooking({ ...booking, name: e.target.value })} />
                    <input data-testid="booking-clinic" placeholder={p.ph.clinic} className={fieldClass} value={booking.clinic_name} onChange={(e) => setBooking({ ...booking, clinic_name: e.target.value })} />
                    <input data-testid="booking-email" required type="email" placeholder={p.ph.email} className={fieldClass} value={booking.email} onChange={(e) => setBooking({ ...booking, email: e.target.value })} />
                    <input data-testid="booking-phone" placeholder={p.phoneField} className={fieldClass} value={booking.phone} onChange={(e) => setBooking({ ...booking, phone: e.target.value })} />
                    <input data-testid="booking-date" required type="date" className={`${fieldClass} [color-scheme:dark]`} value={booking.preferred_date} onChange={(e) => setBooking({ ...booking, preferred_date: e.target.value })} />
                    <input data-testid="booking-time" required type="time" className={`${fieldClass} [color-scheme:dark]`} value={booking.preferred_time} onChange={(e) => setBooking({ ...booking, preferred_time: e.target.value })} />
                  </div>
                  <textarea data-testid="booking-notes" placeholder={p.notes} rows={2} className={fieldClass} value={booking.notes} onChange={(e) => setBooking({ ...booking, notes: e.target.value })} />
                  <button type="submit" disabled={loadingB} data-testid="booking-submit" className="w-full bg-gold text-deepblack px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-goldhover transition-colors disabled:opacity-60">
                    {loadingB ? p.booking : p.request}
                  </button>
                </form>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
